import * as Phaser from "phaser-ce"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { Audio } from "../../../utils/Audio"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { TobaccoTourActivityScreen } from "../sprites/TobaccoTourActivityScreen"
import { TobaccoTourCigPart } from "../sprites/TobaccoTourCigParts"
import { loadAssets, TtAtlas, TtAudio, TtImage } from "../TobaccoTourAssets"
import {
    TobaccoTourProgressStore,
    TobaccoTourStages,
} from "../TobaccoTourProgressStore"
import {
    createTobaccoTourMolecule,
    TobaccoTourMolecule,
    TobaccoTourMoleculeTypes,
} from "../TobaccoTourTypes"
import { CigTourParts } from "./CigTourTypes"

export class CigTourState extends Phaser.State {
    selectedParts: Array<CigTourParts> = []
    cigParts: Phaser.Group
    background: Phaser.Group

    private tobaccoPart: TobaccoTourCigPart
    private smokePart: TobaccoTourCigPart
    private chemicalPart: TobaccoTourCigPart
    private filterPart: TobaccoTourCigPart

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorGray(this.game)

        this.background = this.game.add.group()
        this.cigParts = this.game.add.group()

        Sprites.add(this.game, TtImage.MICROSCOPE_SLIDE, {
            x: Scale.x(0.159),
            y: Scale.y(0.335),
            group: this.background,
        })

        Sprites.add(this.game, TtImage.TOUR_CIG, {
            x: Scale.x(0.208),
            y: Scale.y(0.415),
            group: this.background,
        })

        const smokeSprite = Sprites.add(this.game, TtAtlas.CIG_SMOKE, {
            x: Scale.x(0.204),
            y: Scale.y(0.155),
            group: this.background,
        })
        const smoke = "smoke"
        smokeSprite.animations.add(smoke)
        smokeSprite.animations.play(smoke, 30, true)

        this.tobaccoPart = new TobaccoTourCigPart(
            this.game,
            this.cigParts,
            CigTourParts.TOBACCO,
            TtAtlas.TOBACCO_FLAP,
            Scale.x(0.243),
            Scale.y(0.486),
            "Tobacco",
            Scale.x(0.237),
            Scale.y(0.598),
            () => this.playCigPartVideo(this.tobaccoPart),
        )

        this.smokePart = new TobaccoTourCigPart(
            this.game,
            this.cigParts,
            CigTourParts.SMOKE,
            TtAtlas.SMOKE_FLAP,
            Scale.x(0.322),
            Scale.y(0.46),
            "Smoke",
            Scale.x(0.404),
            Scale.y(0.571),
            () => this.playCigPartVideo(this.smokePart),
        )

        this.chemicalPart = new TobaccoTourCigPart(
            this.game,
            this.cigParts,
            CigTourParts.CHEMICAL,
            TtAtlas.CHEMICAL_FLAP,
            Scale.x(0.51),
            Scale.y(0.429),
            "Chemicals",
            Scale.x(0.54),
            Scale.y(0.545),
            () => this.startInteractiveActivity(),
        )

        this.filterPart = new TobaccoTourCigPart(
            this.game,
            this.cigParts,
            CigTourParts.FILTER,
            TtAtlas.FILTER_FLAP,
            Scale.x(0.671),
            Scale.y(0.407),
            "Filter",
            Scale.x(0.716),
            Scale.y(0.517),
            () => this.playCigPartVideo(this.filterPart),
        )

        Sprites.add(this.game, TtImage.MICROSCOPE_VIEW)

        Audio.add(this.game, TtAudio.CIGARETTE, {
            onPlay: () => PhaserUtils.disableGroup(this.cigParts),
            onStop: () => {
                this.tobaccoPart.open()
                this.smokePart.open()
                this.chemicalPart.open()
                this.filterPart.open()
                PhaserUtils.enableGroup(this.cigParts)
            },
        })
    }

    set selected(part: CigTourParts) {
        if (!this.selectedParts.includes(part)) {
            this.selectedParts.push(part)
        }

        if (this.selectedParts.length === 4) {
            TobaccoTourProgressStore.instance.addStage(
                TobaccoTourStages.CIGARETTE,
            )
            this.game.state.start(ActivityKey.TOBACCO_TOUR)
        } else {
            Audio.add(this.game, TtAudio.CLICK_ANOTHER_PART_OF_CIGARETTE, {
                volume: 0.8,
                onPlay: () => PhaserUtils.disableGroup(this.cigParts),
                onStop: () => PhaserUtils.enableGroup(this.cigParts),
            })
        }
    }

    private molecules = (): Array<TobaccoTourMolecule> => [
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.POLONIUM,
            Scale.x(0.313),
            Scale.y(0.219),
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.FORMALDEHYDE,
            Scale.x(0.221),
            Scale.y(0.351),
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.AMMONIA,
            Scale.x(0.34),
            Scale.y(0.556),
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.NICOTINE,
            Scale.x(0.442),
            Scale.y(0.361),
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.CARBON_MONOXIDE,
            Scale.x(0.564),
            Scale.y(0.237),
        ),
        createTobaccoTourMolecule(
            TobaccoTourMoleculeTypes.ARSENIC,
            Scale.x(0.596),
            Scale.y(0.558),
        ),
    ]

    private startInteractiveActivity = () => {
        PhaserUtils.disableGroup(this.cigParts)
        TobaccoTourActivityScreen.displayInteractiveScreen(
            this.game,
            TtImage.TOBACCO_BACKGROUND,
            this.molecules(),
            () => this.finishCigPart(this.chemicalPart),
            TtAudio.CHEMICAL_AUDIO,
        )
    }

    private playCigPartVideo = (part: TobaccoTourCigPart) => {
        PhaserUtils.disableGroup(this.cigParts)
        TobaccoTourActivityScreen.playVideo(
            this.game,
            part.getPartAsset(),
            () => this.finishCigPart(part),
        )
    }

    private finishCigPart = (part: TobaccoTourCigPart) => {
        PhaserUtils.enableGroup(this.cigParts)
        part.setPartClicked()
        this.selected = part.cigPart
    }
}
